import { ChevronLeftIcon, Save, X } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "../../utils/tailwindUtils";
import { Input } from "../../components/ui/input";
import { DatePicker, Select } from "antd";
import { Button } from "../../components/ui/button";
import { ASSET_BLOCK, ASSET_CITY, ASSET_FLOOR, ASSET_MANAGEMENT, ASSET_REGISTRY, ASSET_ROOM, MASTER_ASSET_CATEGORY, MASTER_ASSET_SUBCATEGORY } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { assetConditionEnum, assetRegistryStatusEnum } from "../../constants/enum";
import dayjs from "dayjs";

const initialData = {
    assetCategory: "",
    assetSubCategory: "",
    purchaseDate: "",
    is_active: true,

    asset_id: "",
    asset_name: "",
    category_id: "",
    sub_category_id: "",
    asset_type_id: "",
    condition: "",
    asset_status: "",
    asset_barcode_no: "",
    model_no: "",
    model_name: "",
    branch: "",
    city_id: "",
    block_id: "",
    floor_id: "",
    room_id: "",
    asset_specification: "",

    // Add missing fields from old code
    purchase_date: "",
    purchase_value: "",
    installation_date: "",
    warranty_expired_on: "",
    equipment_ref_number: "",
    remaining_period: "",
    depreciation_value: "",
    last_service_date: "",
    next_service_date: "",
    insurance_policy_no: "",
    insurance_name: "",
    insurance_from_date: "",
    insurance_to_date: "",
    insurance_company_name: "",
    insurance_value: "",
    is_draft: false,
    company_id: ""
};

const AssetRegistryAddEdit = () => {
    const { fetch, create, update } = useApiMethods();
    const [formData, setFormData] = useState(initialData);
    const [dropdownData, setDropdownData] = useState({
        assetTypeList: [],
        assetCategoryList: [],
        assetSubCategoryList: [],
        assetCityList: [],
        assetBlockList: [],
        assetFloorList: [],
        assetRoomList: [],
    });

    const [loading, setLoading] = useState(false);
    const [hasFormData, setHasFormData] = useState(false);
    const [purchaseDateValue, setPurchaseDate] = useState(null);
    const [warrentyExpiredValue, setWarrentyExpiredDate] = useState(null);
    const [remainingPriodValue, setRemainingPriodValue] = useState(null);
    const [isDisabled, setIsDisabled] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const prevPropsRef = useRef();

    // extract passed values (if any)
    const { fromEdit = false, editDetails = null } = location.state || {};
    const companyid = sessionStorage.getItem("userId");
    const dateFormat = 'YYYY-MM-DD';

    const getAssetCategory = useCallback(async () => {
        try {
            const apiData = await fetch(MASTER_ASSET_CATEGORY, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData;
            } else {
                ToastMsg('error', 'Failed to load categories');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getSubAssetCategory = useCallback(async () => {
        try {
            const apiData = await fetch(MASTER_ASSET_SUBCATEGORY, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData;
            } else {
                ToastMsg('error', 'Failed to load sub categories');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getAssetType = useCallback(async () => {
        try {
            const apiData = await fetch(ASSET_MANAGEMENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData;
            } else {
                ToastMsg('error', 'Failed to load asset types');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getCityData = useCallback(async () => {
        try {
            const apiData = await fetch(ASSET_CITY, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData;
            } else {
                ToastMsg('error', 'Failed to load cities');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getBlockData = useCallback(async () => {
        try {
            const apiData = await fetch(ASSET_BLOCK, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData;
            } else {
                ToastMsg('error', 'Failed to load blocks');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getFloorData = useCallback(async () => {
        try {
            const apiData = await fetch(ASSET_FLOOR, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData;
            } else {
                ToastMsg('error', 'Failed to load floors');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getRoomData = useCallback(async () => {
        try {
            const apiData = await fetch(ASSET_ROOM, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData;
            } else {
                ToastMsg('error', 'Failed to load rooms');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const fetchDropdownData = async () => {
        setLoading(true);
        try {
            const [
                assetList,
                assetCategoryList,
                assetSubCategoryList,
                cityList,
                blockList,
                floorList,
                roomList,
            ] = await Promise.all([
                getAssetType(),
                getAssetCategory(),
                getSubAssetCategory(),
                getCityData(),
                getBlockData(),
                getFloorData(),
                getRoomData(),
            ]);

            setDropdownData({
                assetTypeList: assetList.data || [],
                assetCategoryList: assetCategoryList.data || [],
                assetSubCategoryList: assetSubCategoryList.data || [],
                assetCityList: cityList.data || [],
                assetBlockList: blockList.data || [],
                assetFloorList: floorList.data || [],
                assetRoomList: roomList.data || [],
            });

        } catch (error) {
            ToastMsg('error', error.message);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchDropdownData();
    }, []);

    // Enhanced edit data population
    useEffect(() => {
        if (fromEdit && editDetails &&
            Object.keys(editDetails).length !== 0 &&
            JSON.stringify(prevPropsRef.current) !== JSON.stringify(editDetails)) {

            const editData = {
                ...editDetails,
                purchase_date: editDetails.purchase_date || "",
                warranty_expired_on: editDetails.warranty_expired_on || "",
                installation_date: editDetails.installation_date || "",
                last_service_date: editDetails.last_service_date || "",
                next_service_date: editDetails.next_service_date || "",
                insurance_from_date: editDetails.insurance_from_date || "",
                insurance_to_date: editDetails.insurance_to_date || "",
                company_id: companyid
            };

            setFormData(editData);

            // Set date values for calculations
            if (editDetails.purchase_date) {
                setPurchaseDate(dayjs(editDetails.purchase_date, dateFormat));
            }
            if (editDetails.warranty_expired_on) {
                setWarrentyExpiredDate(dayjs(editDetails.warranty_expired_on, dateFormat));
            }

            // Calculate remaining period if both dates exist
            if (editDetails.purchase_date && editDetails.warranty_expired_on) {
                calculateRemainingPeriod(
                    dayjs(editDetails.purchase_date, dateFormat),
                    dayjs(editDetails.warranty_expired_on, dateFormat)
                );
            }

            prevPropsRef.current = editDetails;
        }
    }, [fromEdit, editDetails, companyid]);

    // Check if form has data for draft saving
    useEffect(() => {
        const hasData = areValuesNotEmptyExceptKeys(formData, ['is_active', 'company_id']);
        setHasFormData(hasData);
    }, [formData]);

    // Enhanced handleInputChange with dependencies
    const handleInputChange = (key, value) => {
        let newFormData = { ...formData };

        // Handle category change - clear sub category
        if (key === 'category_id') {
            newFormData = {
                ...newFormData,
                [key]: value,
                sub_category_id: ""
            };
        }
        // Handle date changes
        else if (key === 'purchase_date') {
            newFormData = {
                ...newFormData,
                [key]: value
            };
            setPurchaseDate(value);
            onFromChange(value);
        }
        else if (key === 'warranty_expired_on') {
            newFormData = {
                ...newFormData,
                [key]: value
            };
            setWarrentyExpiredDate(value);
            onToChange(value);
        }
        else {
            newFormData = {
                ...newFormData,
                [key]: value
            };
        }

        setFormData(newFormData);
    };

    // Date change handlers
    const onFromChange = (value) => {
        setFormData(prev => ({
            ...prev,
            remaining_period: ""
        }));
        setRemainingPriodValue(null);

        if (warrentyExpiredValue && value) {
            calculateRemainingPeriod(value, warrentyExpiredValue);
        }

        // Clear dependent dates if purchase date is cleared
        if (!value) {
            setFormData(prev => ({
                ...prev,
                installation_date: "",
                warranty_expired_on: "",
                last_service_date: "",
                next_service_date: "",
                insurance_from_date: "",
                insurance_to_date: "",
            }));
            setWarrentyExpiredDate(null);
        }
    };

    const onToChange = (value) => {
        setFormData(prev => ({
            ...prev,
            remaining_period: ""
        }));
        setRemainingPriodValue(null);

        if (purchaseDateValue && value) {
            calculateRemainingPeriod(purchaseDateValue, value);
        }
    };

    // Calculate remaining period function
    const calculateRemainingPeriod = (start, end) => {
        if (dayjs(end).isAfter(dayjs(), 'day')) {
            const endDateTime = new Date(end);
            const currentDateTime = new Date();

            let yearsDiff = endDateTime.getFullYear() - currentDateTime.getFullYear();
            let monthsDiff = endDateTime.getMonth() - currentDateTime.getMonth();
            let daysDiff = endDateTime.getDate() - currentDateTime.getDate();

            const setDaysDif = Math.floor((endDateTime - currentDateTime) / (1000 * 60 * 60 * 24));
            setRemainingPriodValue(setDaysDif);

            if (daysDiff < 0) {
                const daysInMonth = new Date(currentDateTime.getFullYear(), currentDateTime.getMonth() + 1, 0).getDate();
                daysDiff = daysInMonth - currentDateTime.getDate() + endDateTime.getDate();
                monthsDiff--;
            }
            if (monthsDiff < 0) {
                monthsDiff += 12;
                yearsDiff--;
            }

            let remainingPeriod = [];
            if (yearsDiff > 0) {
                remainingPeriod.push(`${yearsDiff} year${yearsDiff > 1 ? 's' : ''}`);
            }
            if (monthsDiff > 0) {
                remainingPeriod.push(`${monthsDiff} month${monthsDiff > 1 ? 's' : ''}`);
            }
            if (daysDiff > 0) {
                remainingPeriod.push(`${daysDiff} day${daysDiff > 1 ? 's' : ''}`);
            }

            const formattedRemainingPeriod = remainingPeriod.join(' ');

            setFormData(prev => ({
                ...prev,
                remaining_period: formattedRemainingPeriod
            }));

            return formattedRemainingPeriod;
        } else {
            setFormData(prev => ({
                ...prev,
                remaining_period: ""
            }));
            return "";
        }
    };

    // Date restriction function
    const disabledDateFrom = (current) => {
        return current && current > dayjs().endOf('day');
    };

    // Input restriction for numbers only
    const handleKeyChange = (e) => {
        if (!/^\d$/.test(e.key) &&
            e.key !== "Backspace" &&
            e.key !== "Delete" &&
            e.key !== "ArrowLeft" &&
            e.key !== "ArrowRight" &&
            e.key !== "Home" &&
            e.key !== "End" &&
            e.key !== "Tab") {
            e.preventDefault();
        }
    };

    // Navigation handlers
    const handleBack = () => {
        navigate("/assetmanagement/assetregistry");
    };

    const handleCancel = () => {
        handleBack();
    };

    // Enhanced save functions with API integration
    const handleSubmit = async (isDraftClked = false) => {
        setLoading(true);
        setIsDisabled(true);

        try {
            // Basic validation for draft
            if (isDraftClked && !formData.category_id) {
                ToastMsg("error", "Please select a category before saving as draft.");
                setLoading(false);
                setIsDisabled(false);
                return;
            }

            // Transform data for API with correct field name mapping
            const submitData = {
                // Map field names to match backend expectations
                id: formData.id || undefined,
                is_active: formData.is_active,
                company_id: companyid,
                asset_id: formData.asset_id,
                asset_name: formData.asset_name,
                category: formData.category_id,
                sub_category: formData.sub_category_id,
                asset_type: formData.asset_type_id,
                condition: formData.condition,
                asset_status: formData.asset_status,
                city: formData.city_id,
                block: formData.block_id,
                floor: formData.floor_id,
                room: formData.room_id,

                // Optional fields
                asset_barcode_no: formData.asset_barcode_no || null,
                asset_specification: formData.asset_specification || null,
                branch: formData.branch || null,
                model_no: formData.model_no || null,
                model_name: formData.model_name || null,
                purchase_value: formData.purchase_value || null,
                equipment_ref_number: formData.equipment_ref_number || null,
                depreciation_value: formData.depreciation_value || null,
                insurance_policy_no: formData.insurance_policy_no || null,
                insurance_name: formData.insurance_name || null,
                insurance_company_name: formData.insurance_company_name || null,
                insurance_value: formData.insurance_value || null,

                is_draft: isDraftClked,

                // Format dates for API
                purchase_date: formData.purchase_date ? dayjs(formData.purchase_date).format(dateFormat) : null,
                installation_date: formData.installation_date ? dayjs(formData.installation_date).format(dateFormat) : null,
                warranty_expired_on: formData.warranty_expired_on ? dayjs(formData.warranty_expired_on).format(dateFormat) : null,
                last_service_date: formData.last_service_date ? dayjs(formData.last_service_date).format(dateFormat) : null,
                next_service_date: formData.next_service_date ? dayjs(formData.next_service_date).format(dateFormat) : null,
                insurance_from_date: formData.insurance_from_date ? dayjs(formData.insurance_from_date).format(dateFormat) : null,
                insurance_to_date: formData.insurance_to_date ? dayjs(formData.insurance_to_date).format(dateFormat) : null,
                remaining_period: formData.remaining_period ? remainingPriodValue : null
            };

            // console.log("Final Payload:", submitData);

            const apiFunction = fromEdit ? update : create;

            const response = await apiFunction(ASSET_REGISTRY, submitData);

            if (response?.status === "success") {
                ToastMsg("success", response.message);
                handleBack();
            } else {
                ToastMsg("error", response?.message || "Failed to save asset");
            }

        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
            setIsDisabled(false);
        }
    };

    const handleSave = () => {
        handleSubmit(false);
    };

    const handleSaveAsDraft = () => {
        handleSubmit(true);
    };

    // Utility function to check if form has data
    const areValuesNotEmptyExceptKeys = (obj, keysToExclude) => {
        return Object.keys(obj).some(key =>
            !keysToExclude.includes(key) &&
            obj[key] !== "" &&
            obj[key] !== null &&
            obj[key] !== undefined
        );
    };

    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        )
    }

    return (
        <ContainerCard>
            {/* Header Section */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer"
                    onClick={handleBack}
                />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Asset Registry" : "Add Asset Registry"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {fromEdit
                            ? "Update existing asset registry details."
                            : "Register a new asset and its details."}
                    </p>
                </div>

                {/* Active Status Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center gap-3">
                        <span className={cn(
                            "text-sm font-medium",
                            formData.is_active
                                ? "text-green-600 dark:text-green-400"
                                : "text-gray-500 dark:text-gray-400"
                        )}>
                            {formData.is_active ? "Active" : "Inactive"}
                        </span>
                        <button
                            type="button"
                            onClick={() => handleInputChange('is_active', !formData.is_active)}
                            className={cn(
                                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                !fromEdit && !formData.is_active ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                            )}
                            disabled={!fromEdit && !formData.is_active}
                        >
                            <span
                                className={cn(
                                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                    formData.is_active ? "translate-x-4" : "translate-x-1"
                                )}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* Form Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
                    {/* Asset ID */}
                    {
                        fromEdit ? (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Asset ID <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    value={formData.asset_id}
                                    onChange={(e) => handleInputChange('asset_id', e.target.value)}
                                    placeholder="Enter asset ID"
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                />
                            </div>
                        ) : null
                    }

                    {/* Asset Name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Asset Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={formData.asset_name}
                            onChange={(e) => handleInputChange('asset_name', e.target.value)}
                            placeholder="Enter asset name"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Category */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Category <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.category_id}
                            onChange={(value) => handleInputChange('category_id', value)}
                            size="large"
                            placeholder="Select category"
                            options={dropdownData.assetCategoryList.map((category) => ({
                                label: category.category_name,
                                value: category.id,
                            }))}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Sub Category */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Sub Category <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.sub_category_id}
                            onChange={(value) => handleInputChange('sub_category_id', value)}
                            size="large"
                            placeholder="Select sub category"
                            options={dropdownData.assetSubCategoryList.map((subCategory) => ({
                                label: subCategory.asset_sub_category_name,
                                value: subCategory.id,
                            }))}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Asset Type */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Asset Type <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.asset_type_id}
                            onChange={(value) => handleInputChange('asset_type_id', value)}
                            size="large"
                            placeholder="Select asset type"
                            options={dropdownData.assetTypeList.map((type) => ({
                                label: type.asset_type_name,
                                value: type.id,
                            }))}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Condition */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Condition <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.condition}
                            onChange={(value) => handleInputChange('condition', value)}
                            size="large"
                            placeholder="Select condition"
                            options={assetConditionEnum.map(status => ({
                                label: status.text,
                                value: status.value
                            }))}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Status <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.asset_status}
                            onChange={(value) => handleInputChange('asset_status', value)}
                            size="large"
                            placeholder="Select status"
                            options={assetRegistryStatusEnum.map(status => ({
                                label: status.text,
                                value: status.value
                            }))}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Asset Barcode/No */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Asset Barcode/No
                        </label>
                        <Input
                            value={formData.asset_barcode_no}
                            onChange={(e) => handleInputChange('asset_barcode_no', e.target.value)}
                            placeholder="Enter barcode number"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Model No */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Model No
                        </label>
                        <Input
                            value={formData.model_no}
                            onChange={(e) => handleInputChange('model_no', e.target.value)}
                            placeholder="Enter model number"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Model Name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Model Name
                        </label>
                        <Input
                            value={formData.model_name}
                            onChange={(e) => handleInputChange('model_name', e.target.value)}
                            placeholder="Enter model name"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Branch */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Branch
                        </label>
                        <Input
                            value={formData.branch}
                            onChange={(e) => handleInputChange('branch', e.target.value)}
                            placeholder="Enter branch"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* City */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            City <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.city_id}
                            onChange={(value) => handleInputChange('city_id', value)}
                            placeholder="Select city"
                            size="large"
                            options={dropdownData.assetCityList.map(city => ({
                                label: city.city_name,
                                value: city.id,
                            }))}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Block */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Block <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.block_id}
                            onChange={(value) => handleInputChange('block_id', value)}
                            placeholder="Select block"
                            size="large"
                            options={dropdownData.assetBlockList.map(block => ({
                                label: block.block,
                                value: block.id,
                            }))}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Floor */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Floor <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.floor_id}
                            onChange={(value) => handleInputChange('floor_id', value)}
                            placeholder="Select floor"
                            size="large"
                            options={dropdownData.assetFloorList.map(floor => ({
                                label: floor.floor,
                                value: floor.id,
                            }))}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Room */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Room <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.room_id}
                            onChange={(value) => handleInputChange('room_id', value)}
                            placeholder="Select room"
                            size="large"
                            options={dropdownData.assetRoomList.map(room => ({
                                label: room.room,
                                value: room.id,
                            }))}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Asset Specification */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Asset Specification
                        </label>
                        <Input
                            value={formData.asset_specification}
                            onChange={(e) => handleInputChange('asset_specification', e.target.value)}
                            placeholder="Enter asset specification"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>
                </div>

                <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Purchase Details</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
                    {/* Purchase date */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Purchase Date
                        </label>
                        <DatePicker
                            value={purchaseDateValue}
                            onChange={(value) => handleInputChange('purchase_date', value)}
                            disabledDate={disabledDateFrom}
                            format={dateFormat}
                            size="large"
                            placeholder="Enter date"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Purchase Value */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Purchase Value
                        </label>
                        <Input
                            value={formData.purchase_value}
                            onChange={(e) => handleInputChange('purchase_value', e.target.value)}
                            onKeyDown={handleKeyChange}
                            placeholder="Enter value"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Installation Date */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Installation Date
                        </label>
                        <DatePicker
                            value={formData.installation_date ? dayjs(formData.installation_date, dateFormat) : null}
                            onChange={(value) => handleInputChange('installation_date', value)}
                            format={dateFormat}
                            size="large"
                            placeholder="Enter date"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Warranty Expired Date */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Warranty Expired Date
                        </label>
                        <DatePicker
                            value={warrentyExpiredValue}
                            onChange={(value) => handleInputChange('warranty_expired_on', value)}
                            format={dateFormat}
                            size="large"
                            placeholder="Enter date"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Equipment Reference No */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Equipment Reference No
                        </label>
                        <Input
                            value={formData.equipment_ref_number}
                            onChange={(e) => handleInputChange('equipment_ref_number', e.target.value)}
                            placeholder="Enter value"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Warranty Remaining Period */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Warranty Remaining Period
                        </label>
                        <Input
                            value={formData.remaining_period}
                            readOnly
                            placeholder="Auto calculated"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Depreciation Value / Month */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Depreciation Value / Month
                        </label>
                        <Input
                            value={formData.depreciation_value}
                            onChange={(e) => handleInputChange('depreciation_value', e.target.value)}
                            onKeyDown={handleKeyChange}
                            placeholder="Enter value"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Last Service Date */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Last Service Date
                        </label>
                        <DatePicker
                            value={formData.last_service_date ? dayjs(formData.last_service_date, dateFormat) : null}
                            onChange={(value) => handleInputChange('last_service_date', value)}
                            format={dateFormat}
                            size="large"
                            placeholder="Enter date"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Next Service Date */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Next Service Date
                        </label>
                        <DatePicker
                            value={formData.next_service_date ? dayjs(formData.next_service_date, dateFormat) : null}
                            onChange={(value) => handleInputChange('next_service_date', value)}
                            format={dateFormat}
                            size="large"
                            placeholder="Enter date"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>
                </div>

                <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Insurance Details</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
                    {/* Policy no */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Policy No
                        </label>
                        <Input
                            value={formData.insurance_policy_no}
                            onChange={(e) => handleInputChange('insurance_policy_no', e.target.value)}
                            placeholder="Enter policy code"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Insured Name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Insured Name
                        </label>
                        <Input
                            value={formData.insurance_name}
                            onChange={(e) => handleInputChange('insurance_name', e.target.value)}
                            placeholder="Enter value"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Insurance From date */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Insurance From Date
                        </label>
                        <DatePicker
                            value={formData.insurance_from_date ? dayjs(formData.insurance_from_date, dateFormat) : null}
                            onChange={(value) => handleInputChange('insurance_from_date', value)}
                            format={dateFormat}
                            size="large"
                            placeholder="Enter date"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Insurance To date */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Insurance To Date
                        </label>
                        <DatePicker
                            value={formData.insurance_to_date ? dayjs(formData.insurance_to_date, dateFormat) : null}
                            onChange={(value) => handleInputChange('insurance_to_date', value)}
                            format={dateFormat}
                            size="large"
                            placeholder="Enter date"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Insurance company name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Insurance Company Name
                        </label>
                        <Input
                            value={formData.insurance_company_name}
                            onChange={(e) => handleInputChange('insurance_company_name', e.target.value)}
                            placeholder="Enter value"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Insurance Value */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Insurance Value
                        </label>
                        <Input
                            value={formData.insurance_value}
                            onChange={(e) => handleInputChange('insurance_value', e.target.value)}
                            onKeyDown={handleKeyChange}
                            placeholder="Enter value"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading || isDisabled}
                        className="flex items-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </Button>

                    {/* Draft Button - Only show in create mode */}
                    {!fromEdit && (
                        <Button
                            onClick={handleSaveAsDraft}
                            disabled={loading || isDisabled || !hasFormData}
                            className={cn(
                                "bg-gray-600 hover:bg-gray-700",
                                "text-white transition-colors duration-200",
                                "flex items-center gap-2",
                                (!hasFormData) && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            Save as Draft
                        </Button>
                    )}

                    <Button
                        onClick={handleSave}
                        disabled={loading || isDisabled}
                        className={cn(
                            "bg-sidebar hover:bg-sidebar-primary",
                            "dark:bg-blue-600 dark:hover:bg-blue-700",
                            "text-white transition-colors duration-200",
                            "flex items-center gap-2"
                        )}
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {loading ? "Saving..." : (fromEdit ? "Update" : "Save")}
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
};

export default AssetRegistryAddEdit;